import { Spinner } from '@fluxora/ui';
import { useTranslation } from '@fluxora/i18n';

import { useAgreementReadinessQuery } from '../api/status.queries';
import { SignedAgreementDownload } from './SignedAgreementDownload';

/**
 * The signed-contract download on the confirmation screen, for an enrollment whose
 * contract is still being written (E-SF06).
 *
 * ── WHY THIS EXISTS ────────────────────────────────────────────────────────
 * The storefront accept is asynchronous. When this screen first renders the
 * SIGNATURE is durable but the CONTRACT is not: the signature worker creates the
 * account, renders the three regulated PDFs, archives them and commits the
 * contract moments later. Offering the download in that window produces a request
 * that 404s and a spinner that never resolves — at the exact moment the customer
 * is looking for reassurance that their signing worked.
 *
 * So the control is always PRESENT (the customer can see the contract is coming)
 * and only becomes usable once the server says the document exists.
 *
 * ── WHY IT POLLS RATHER THAN GUESSING ──────────────────────────────────────
 * `agreementAvailable` is an explicit field on the status response, computed from
 * the live quote's `contractUid` + `signedAt`. It is NOT inferred from the
 * lifecycle state here: PENDING covers several situations, and coupling a UI
 * affordance to a lifecycle label turns any future lifecycle change into a silent
 * download bug.
 *
 * ── WHY THE WAIT IS BOUNDED ────────────────────────────────────────────────
 * The poll stops after ten minutes. Past that the worker's own stuck-submission
 * alarm has already fired, so continuing to spin would tell the customer a
 * comforting thing that is not true. The copy then defers to the email, which is
 * the channel that survives them closing the tab.
 */
export function AgreementDownloadWhenReady({
  statusToken,
}: {
  readonly statusToken: string;
}): JSX.Element {
  const { t } = useTranslation();
  const status = useAgreementReadinessQuery(statusToken);

  if (status.data?.agreementAvailable === true) {
    return <SignedAgreementDownload statusToken={statusToken} />;
  }

  // Past the early return the contract is known NOT to be available, so the only
  // question left is whether anything is still going to change. `isFetched` means
  // we have had at least one answer; `!isFetching` means no request is in flight —
  // together, the poll has stopped and this screen is now final.
  const gaveUp = status.isFetched && !status.isFetching;

  return (
    <div className="flex flex-col gap-1">
      <span
        className="flex w-fit items-center gap-2 text-sm text-foreground/70"
        // A live region, because the swap from "preparing" to a usable download
        // happens without any interaction — a screen-reader user would otherwise
        // never learn their contract had arrived.
        role="status"
        aria-live="polite"
      >
        <Spinner size="sm" aria-hidden="true" />
        {t('shop.sign.confirm.contractPreparing')}
      </span>
      {gaveUp ? (
        <span className="text-xs text-foreground/60">
          {t('shop.sign.confirm.downloadStillPreparing')}
        </span>
      ) : null}
    </div>
  );
}
